import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type AddressFormatterAddress = {
  city?: string | null
  state?: string | null
  postal_code?: string | null
  name?: string | null
  line1?: string | null
  line2?: string | null
  country?: string | null
}

export type AddressFormatterVariant =
  | 'full'
  | 'short'
  | 'inline'
  | 'noCountry'
  | 'compact'
  | 'recipientFocused'
  | 'inlineLineOne'

export type AddressFormatterProps = {
  address?: AddressFormatterAddress
  variant?: AddressFormatterVariant
  icon?: ReactNode
  name?: string | null
  isLink?: boolean
  className?: string
}

export function AddressFormatter({
  address = {
    city: '',
    state: '',
    postal_code: '',
    name: '',
    line1: '',
    line2: '',
    country: '',
  },
  variant = 'full',
  icon = null,
  name = '',
  isLink = false,
  className = '',
}: AddressFormatterProps) {
  const cityLine = `${address.city}${address.state ? `, ${address.state}` : ''}${address.postal_code ? ` ${address.postal_code}` : ''}`
  const nameLine = name ? `${name}` : address.name ? `${address.name}` : null

  const formatAddress = () => {
    switch (variant) {
      case 'short':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              <p className="flex justify-start items-center gap-1">{cityLine}</p>
            </div>
          </div>
        )

      case 'inline':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              <p>
                {[
                  nameLine,
                  address.line1,
                  address.line2,
                  cityLine,
                  address.country !== 'US' ? address.country : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </div>
          </div>
        )

      case 'noCountry':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              {address.line1 && <p>{address.line1}</p>}
              {address.line2 && <p>{address.line2}</p>}
              <p>{cityLine}</p>
            </div>
          </div>
        )

      case 'compact':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              {[address.line1, address.city, address.state].filter(Boolean).join(', ')}
            </div>
          </div>
        )

      case 'recipientFocused':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              <p>
                {address.city}, {address.state}
              </p>
            </div>
          </div>
        )

      case 'inlineLineOne':
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              <p>{address.line1}</p>
            </div>
          </div>
        )

      case 'full':
      default:
        return (
          <div className={cn('grid grid-cols-[auto_1fr] gap-x-1', className)}>
            {icon && <div className="row-span-3 flex items-start pt-1">{icon}</div>}
            <div className="flex flex-col gap-1 mt-1">
              {nameLine && <p className="font-bold">{nameLine}</p>}
              {address.line1 && <p>{address.line1}</p>}
              {address.line2 && <p>{address.line2}</p>}
              <p>{cityLine}</p>
              <p>{address.country}</p>
            </div>
          </div>
        )
    }
  }

  if (isLink) {
    return (
      <a
        href={`https://maps.google.com/?q=${address.line1}, ${address.city}, ${address.state}, ${address.postal_code}`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-interaction"
      >
        {formatAddress()}
      </a>
    )
  }

  return formatAddress()
}
